import React, {
  useEffect,
  useState,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components/macro';
import { RootState } from 'redux/rootReducer';

import SearchableDropdown from 'components/base/Dropdown/SearchableDropdown';
import {
  IUserSettingsState,
  loadOrInitUserSettings,
  shouldLoadUserSettings,
} from 'redux/reducers/userSettings';
import { saveUserDivision } from 'redux/reducers/user/updateUserDivision';
import { saveUserLabel } from 'redux/reducers/user/updateUserLabel';

import { ListItem } from '../Location';

import getDivisions from './getDivisions';
import getLabels from './getLabels';

const ConfirmLocationWrapper = styled('div')<any>`
  display: flex;
  flex-direction: column;
  gap: 24px;
  ${({ alignLeft }): string => (alignLeft ? 'align-items: end;' : '')}
}
`;
const DropdownWrapper = styled('div')`
  max-width: fit-content;
`;

interface IDropdownDivision {
  label: string;
  value: string;
  icon: any;
}

const Division = forwardRef<any, { alignLeft?: boolean }>(
  ({ alignLeft }, ref) => {
    const dispatch = useDispatch();

    const divisions = getDivisions();
    const labels = getLabels();

    const [selectedDivision, setSelectedDivision] = useState({
      label: 'Select Division',
      value: '',
    });
    const [selectedLabels, setSelectedLabel] = useState<any[]>([]);

    const userSettings = useSelector(
      (state: RootState): IUserSettingsState => state.userSettings
    );

    const userDivisionFromSettings = userSettings?.data?.userDivision;
    const userLabelFromSettings = userSettings?.data?.userLabel;

    useEffect(() => {
      if (!selectedDivision.value && userDivisionFromSettings) {
        const divisionFromSettings = divisions.find(
          (division: any) => division.value === userDivisionFromSettings
        );
        setSelectedDivision(divisionFromSettings);
      }
    }, [userDivisionFromSettings]);

    useEffect(() => {
      if (!selectedLabels.length && userLabelFromSettings?.length) {
        const labelFromSettings = userLabelFromSettings.reduce(
          (filtered: any, option: any) => {
            const selectedLabel = labels.find(
              (label: any) => label.value === option
            );
            if (selectedLabel) {
              filtered.push(selectedLabel);
            }
            return filtered;
          },
          []
        );

        setSelectedLabel(labelFromSettings);
      }
    }, [userLabelFromSettings]);

    useEffect(() => {
      if (dispatch(shouldLoadUserSettings())) {
        dispatch(loadOrInitUserSettings());
      }
    }, [dispatch]);

    const saveDivision = (newDivision: any): void => {
      const userDivisionId = newDivision?.value;
      if (userDivisionId) {
        dispatch(saveUserDivision(userDivisionId));
      }
    };

    const saveLabel = (newLabel: any): void => {
      dispatch(saveUserLabel(newLabel));
    };

    const selectedLabelsIds = useMemo(
      () => selectedLabels?.map((label: any) => label.value),
      [selectedLabels]
    );

    const onChangeLabel = (item: any): void => {
      const isChecked = selectedLabelsIds?.includes(item?.value);

      let newLabelsList = [item?.value];
      if (isChecked && selectedLabelsIds?.length) {
        // remove from current labels
        newLabelsList = selectedLabelsIds?.filter(
          (label: any) => label !== item?.value
        );
      } else if (selectedLabelsIds?.length) {
        // add to current labels
        newLabelsList = [...selectedLabelsIds, item?.value];
      }

      if (newLabelsList.length) {
        const selectedLabel = newLabelsList.reduce(
          (filtered: any, option: any) => {
            const selectedLabel = labels.find(
              (label: any) => label.value === option
            );
            if (selectedLabel) {
              filtered.push(selectedLabel);
            }
            return filtered;
          },
          []
        );

        setSelectedLabel(selectedLabel);
        if (!ref) {
          saveLabel(newLabelsList);
        }
      } else {
        setSelectedLabel([]);
        if (!ref) {
          saveLabel([]);
        }
      }
    };

    useImperativeHandle(ref, () => ({
      externalSave(): void {
        saveDivision(selectedDivision);
        saveLabel(selectedLabelsIds);
      },
    }));

    const onChange = (newDivision: IDropdownDivision): void => {
      setSelectedDivision(newDivision);
      if (!ref) {
        saveDivision(newDivision);
      }
    };

    return divisions ? (
      <ConfirmLocationWrapper alignLeft={alignLeft}>
        <DropdownWrapper>
          <SearchableDropdown
            items={divisions}
            selectedItem={selectedDivision}
            onChange={onChange}
            Item={ListItem}
            disabled={false}
            isActiveGreen={false}
            isRoundedDropdown="all"
          />
        </DropdownWrapper>
        {selectedDivision?.value === 'recordedMusic' ? (
          <DropdownWrapper>
            <SearchableDropdown
              items={labels}
              selectedItem={
                selectedLabels?.length === 1
                  ? selectedLabels[0]
                  : {
                      label: `${
                        selectedLabels?.length === 0
                          ? 'Select Label'
                          : `${selectedLabels?.length} labels`
                      }`,
                      value: '',
                    }
              }
              selectedItems={selectedLabelsIds}
              onChange={onChangeLabel}
              Item={ListItem}
              disabled={false}
              isActiveGreen={false}
              isRoundedDropdown="all"
              isMultipleSelection
            />
          </DropdownWrapper>
        ) : null}
      </ConfirmLocationWrapper>
    ) : null;
  }
);

export default Division;
